/* global window global */
import axios from 'axios'
import _ from 'lodash'
import URLSearchParams from '@ungap/url-search-params'
import { createBrowserHistory } from 'history'
import { batch } from 'react-redux'
import { FT } from '../utils/contextFT'

const convertTsgEntitlements = (accountid, { tsg_id, support_account_id = accountid, instances, apps, hub_url, generated }) => {
  const appsMap = _.keyBy(apps.map(({ normalized_logo_url, rbac_role, ...app }) => Object.assign(app, {
    name: {
      'logging_service': 'Logging Service',
      'directory_sync': 'Directory Sync',
    }[app.app_id] || app.app_id,
    logo: normalized_logo_url,
    isThirdParty: app.is_third_party,
    role: (rbac_role && rbac_role.app_role) || (app.is_third_party ? 'ANY' : app.app_id)
  })), 'app_id')
  const entitlements = instances.reduce((ent, inst) => {
    if (!_.get(appsMap, [inst.app_id, 'enabled'])) {
      return ent
    }
    const app = ent[inst.app_id] || (ent[inst.app_id] = {
      app_id: inst.app_id,
      enabled: true,
      entitled: true,
      ...appsMap[inst.app_id],
      tenants: [],
    })
    const {
      tenant_id,
      provisioning_status,
      status: instance_status = provisioning_status,
      license_expiration: expiration,
      // support_account_id,
      region,
      serial_number,
      tenant_instance_name,
      url,
    } = inst
    app.tenants.push({
      tenant_id,
      // support_account_id,
      instance_status,
      expiration,
      region,
      serial_number,
      tenant_instance_name,
      url,
    })
    return ent
  }, {
    'App Portal': {
      support_account_id,
      // rbac_roles: rbacRoles,
      tsg_id,
      url: hub_url,
      account_info: new URL('/account-info', hub_url).toString(),
      generated,
      instances,
    },
  })
  return entitlements
}

export const initilizeApps = (apps = []) => ({
  type: 'INITIALIZE_APPS',
  apps
})

export const fetchCredential = (options) => (dispatch, getState) => {
  const loading = options?.loading
  dispatch({
    type: 'FETCH_CREDENTIALS_START',
    loading,
  })
  const urlQueries = new URLSearchParams(window.location.search)
  return axios.get('/saml/credentials', {
    params: {
      instance: urlQueries.get('instance')
    }
  }).then(
    response => {
      const { supportAccountIds = [], notonorafter, defaultsupportaccount } = response?.data?.credentials || {}
      // for easy debugging api calls
      global._xSigs = supportAccountIds.map(acct => `${acct.accountid} ${notonorafter} ${acct.signature}`)
      const isAuthenticated = _.get(response, 'data.isAuthenticated')
      if (isAuthenticated !== true) {
        dispatch({
          type: 'FETCH_CREDENTIALS_NO_AUTH'
        })
        return
      }
      const { selectedAccount } = getState()
      let selectedAccountIdx = +selectedAccount || 0
      if (_.has(response, 'data.selectedAccount')) {
        selectedAccountIdx = +_.get(response, 'data.selectedAccount', 0)
      }
      else if (urlQueries.has('selectedAccount')) {
        selectedAccountIdx = urlQueries.get('selectedAccount')
        const history = createBrowserHistory()
        urlQueries.delete('selectedAccount')
        history.replace(`${history.location.pathname}?${urlQueries.toString()}`) // remove selectedAccount
      }
      else if (!selectedAccountIdx && defaultsupportaccount > 0 && !localStorage.selectedAccount) {
        const defaultCsp = +defaultsupportaccount
        const index = supportAccountIds.findIndex(a => +a.accountid === defaultCsp)
        selectedAccountIdx = index >= 0 ? index : 0
      }
      if (selectedAccountIdx > 0 ? !supportAccountIds[selectedAccountIdx] : !+selectedAccountIdx) {
        if (selectedAccountIdx > 0) {
          // eslint-disable-next-line no-console
          console.error(`selected account ${selectedAccountIdx} is not available, fallback to 0`)
        }
        selectedAccountIdx = 0
      }
      batch(() => {
        dispatch(setSupportAccountIndex(selectedAccountIdx, { loading }))
        dispatch({
          type: 'FETCH_CREDENTIALS_SUCCESS',
          loading,
          selectedAccount: selectedAccountIdx,
          data: response.data,
        })
      })
    },
    error => {
      dispatch({
        type: 'FETCH_CREDENTIALS_FAILURE',
        message: error.message || 'Something went wrong.'
      })
    }
  )
}

export const setSupportAccountIndex = (id, options) => (dispatch, getState) => {
  const { supportAccountIds, selectedAccount, isAuthenticated } = getState()
  if (isAuthenticated && id > 0 && !supportAccountIds[id]) {
    // eslint-disable-next-line no-console
    console.error(`selected account ${id} is not available, fallback to 0`)
    id = 0
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('selectedAccount', id)
  }
  if (+id !== +selectedAccount) {
    dispatch({ type: 'SET_CURRENT_SUPPORT_ACCOUNT_INDEX', ...options, id, supportAccountIds, selectedAccount })
  }
}

export const setAppBarProps = appBarProps => (dispatch) => {
  dispatch({
    type: 'SET_APP_BAR_PROPS',
    appBarProps
  })
}

export const setIsCollapsed = isCollapsed => (dispatch) => {
  dispatch({
    type: 'APP_BAR_COLLAPSED',
    isCollapsed
  })
}

export const fetchEntitlements = (options = {}) => (dispatch, getState) => {
  const {
    entitledAppsList,
    selectedAccount: curSelectedAccount,
    supportAccountIds,
    notonorafter,
    jwtToken: { accesstoken, pingcert } = {},
    user_email,
    portalSettings,
    appLogos,
  } = getState()
  const { selectedAccount = curSelectedAccount, logo = !appLogos } = options
  const { path, app_id: app = path } = portalSettings || {}
  const hash = selectedAccount === curSelectedAccount && entitledAppsList._hash // ignore hash if account switched

  if (_.isEmpty(supportAccountIds) || !_.has(supportAccountIds, selectedAccount)) {
    return // skip if no accounts avail
  }

  const isHub = app === 'app' || app === 'hub'
  const auth = !(isHub || FT('ENTITLEMENTS_API_WITH_AUTH'))
  const { signature, accountid } = supportAccountIds[selectedAccount]
  const params = auth ? {
    notonorafter,
    support_account_id: accountid,
    user_email,
    signature,
    app,
    logo,
  } : { support_account_id: accountid }
  if (auth && accesstoken && pingcert) {
    params.accesstoken = accesstoken
    params.pingcert = pingcert
    params.roles = undefined
  }
  const urlQueries = new URLSearchParams(window.location.search)
  const tsgId = urlQueries.get('tsg_id')
  if (tsgId) {
    params.tsg_id = tsgId
  }

  if (fetchEntitlements.cancel) {
    fetchEntitlements.cancel()
  }
  const source = axios.CancelToken.source()
  fetchEntitlements.cancel = () => source.cancel('new request')
  const isAccountSwitched = () => {
    const { selectedAccount: curIdx } = getState()
    return curSelectedAccount === selectedAccount && selectedAccount !== curIdx
  }
  const url = isHub ? '/hub/v2/entitlements' : '/api/v1/entitlements'
  return axios.post(url, params, {
    cancelToken: source.token,
    ...(hash && {
      headers: {
        'If-None-Match': hash,
      }
    }),
    validateStatus: status => status === 200 || (hash && status === 304)
  }).then(
    response => {
      fetchEntitlements.cancel = undefined
      if (isAccountSwitched()) {
        return // skip if account switched
      }
      batch(() => {
        dispatch({
          type: 'UPDATE_ROLES',
          data: { supportAccountIds, selectedAccount }
        })
        dispatch({
          type: 'IS_FEDERAL',
          data: { supportAccountIds, selectedAccount }
        })
        if (response.status === 200) {
          let apps = response.data.entitlements
          if (!apps && response.data.tsg_id && response.data.instances && response.data.apps) {
            apps = convertTsgEntitlements(+accountid, response.data)
          }
          dispatch({
            type: 'SET_ENTITLED_APPS',
            apps,
            account: +accountid,
          })
          const { name, app_id } = portalSettings || {}
          const app = _.get(apps, name) || (app_id && _.get(apps, app_id)) ||
            _.values(apps).find(app => app.name === name || (app_id && app.app_id === app_id))
          if (app) {
            dispatch({
              type: 'SET_INSTANCE_NAME',
              app,
            })
          }
        }
        dispatch({ // this action does not have entitlements
          type: 'FETCH_ENTITLEMENTS_SUCCESS',
          status: response.status,
          account: +accountid,
          timestamp: Date.now(),
        })
      })
    },
    error => {
      fetchEntitlements.cancel = undefined
      if (source.token.reason || isAccountSwitched()) {
        return // skip if account switched or canceled
      }
      batch(() => {
        dispatch({
          type: 'UPDATE_ROLES',
          data: { supportAccountIds, selectedAccount }
        })
        dispatch({
          type: 'FETCH_ENTITLEMENTS_FAILURE',
          message: error.message || 'Something went wrong.'
        })
      })
    }
  )
}

export const switchAccountIndex = (idx) => (dispatch) => {
  if (idx >= 0) {
    // if (fetchEntitlements.cancel) {
    //   fetchEntitlements.cancel()
    // }
    batch(() => {
      dispatch(setSupportAccountIndex(idx))
      dispatch(fetchEntitlements({ selectedAccount: idx }))
    })
  }
}
